import { useEffect } from 'react';
import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { ContentBlockType } from '@onesource/schemas';
import { useGetLesson, useGetLessons, useGetProgress, useGetSpace } from '@onesource/services';
import { Link as RouterLink, createFileRoute, useNavigate } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { hasHTML } from '#/utils/has-html';
import { LessonDetailView } from '#/features/community-hub/components/lesson-detail-view';

export const Route = createFileRoute('/_protected/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/')({
  component: LessonDetailRoute,
});

function LessonDetailRoute() {
  const { id: spaceId, moduleId, lessonId } = Route.useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useGetLesson(lessonId);
  const { data: lessonProgress, isLoading: isLoadingProgress } = useGetProgress(lessonId);
  const { data: module, isLoading: isLoadingModule } = useGetSpace(moduleId);
  const { data: lessons, isLoading: isLoadingLessons } = useGetLessons(moduleId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId);

  // Parent-type lessons (those that contain sub-lessons) have no detail view of
  // their own — redirect to the module page where they render as a section.
  const isParentLesson = !!lesson && ((lesson.subLessonCount || 0) > 0 || (lesson.children?.length ?? 0) > 0);

  useEffect(() => {
    if (isParentLesson) {
      navigate({
        to: '/community-hub/spaces/$id/modules/$moduleId',
        params: { id: spaceId, moduleId },
        replace: true,
      });
    }
  }, [isParentLesson, navigate, spaceId, moduleId]);

  if (isLoadingLesson || isLoadingProgress || isLoadingModule || isLoadingLessons || isParentLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load lesson: {lessonError.message}</Alert>
      </Box>
    );
  }

  if (!lesson) {
    return (
      <Box p={3}>
        <Alert severity="error">Lesson not found</Alert>
      </Box>
    );
  }

  const breadcrumbs = (
    <Breadcrumbs
      sx={{
        mb: 2,
        fontFamily: '"Figtree", sans-serif',
        fontSize: { xs: 14, md: 16 },
        lineHeight: { xs: '20px', md: '22px' },
        fontWeight: 400,
        color: theme.palette.grey[600],
        '& .MuiBreadcrumbs-separator': { mx: 0.6 },
      }}
    >
      {module?.parent && (
        <RouterLink
          to="/community-hub/spaces/$id"
          params={{ id: spaceId }}
          style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
        >
          <Typography variant="body2" sx={{ mt: hasHTML(module.parent.name) ? -0.5 : 0, fontSize: { xs: 14, md: 16 } }}>
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.parent.name) }} />
          </Typography>
        </RouterLink>
      )}
      {module && (
        <RouterLink
          to="/community-hub/spaces/$id/modules/$moduleId"
          params={{ id: spaceId, moduleId }}
          style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
        >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.name) }} />
        </RouterLink>
      )}
      {findParentLesson && (
        <RouterLink
          to="/community-hub/spaces/$id/modules/$moduleId"
          params={{ id: spaceId, moduleId }}
          search={{ sectionId: findParentLesson.id }}
          style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
        >
          {findParentLesson?.title}
        </RouterLink>
      )}
      <Typography component="span" sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 700 }}>
        {lesson.title}
      </Typography>
    </Breadcrumbs>
  );

  const contentPathByType: Record<string, string> = {
    [ContentBlockType.VIMEO]: 'video',
    [ContentBlockType.PDF]: 'pdf',
    [ContentBlockType.TEXT]: 'text',
    [ContentBlockType.QUIZ]: 'quiz',
    [ContentBlockType.FORM]: 'form',
    [ContentBlockType.EMBED]: 'embed',
    [ContentBlockType.GCS_UPLOAD]: 'gcs-upload',
  };

  return (
    <LessonDetailView
      lesson={lesson}
      progressPercent={lessonProgress?.progressPercent ?? 0}
      breadcrumbs={breadcrumbs}
      onNavigateToBlock={block => {
        const segment = contentPathByType[block.blockType];
        if (!segment) return;
        navigate({
          to: `/community-hub/spaces/${spaceId}/modules/${moduleId}/lessons/${lessonId}/${segment}/${block.id}`,
        });
      }}
      onNavigateToSubLesson={subLessonId => {
        navigate({ to: `/community-hub/spaces/${spaceId}/modules/${moduleId}/lessons/${subLessonId}` });
      }}
    />
  );
}
